import React, {useEffect, useRef} from 'react';
import {StyleSheet, View} from 'react-native';

import {useInspectorHost} from '@/shared/ui/inspector';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Список слева, выбранная запись — в инспекторе справа.
 *
 * На телефоне открыть человека значит уйти со списка: экран сверху, «назад», место
 * потеряно. На 390pt это правильно — там помещается ровно одна вещь. На мониторе
 * окно вмещает обе, и держать обе — это то, что делает «просмотреть сорок человек и
 * прочитать одного» одной задачей, а не сорока походами туда-обратно.
 *
 * ## Что изменилось
 *
 * Панель больше не рисуется здесь. Раньше это была коробка 400pt рядом с таблицей —
 * с заглушкой «Выберите сотрудника», пока ничего не выбрано, и она стояла всегда,
 * отнимая у таблицы треть окна ради пустой иконки. Теперь запись регистрируется у
 * страницы (`useInspectorHost`), а страница ставит её пристыкованной панелью на всю
 * высоту рабочей области — см. `shared/ui/inspector`. Пока строка не выбрана, панели
 * нет и таблице отдано всё окно.
 *
 * Без хозяина (список смонтирован вне `Page`) запись рисуется по-старому, рядом.
 */
const styles = StyleSheet.create({
  root: {flexDirection: 'row', alignItems: 'flex-start', gap: spacing(5)},
  list: {flex: 1, minWidth: 0},
  pane: {
    width: 400,
    borderRadius: radii.lg,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
    padding: spacing(4),
  },
});

export function SplitView({
  list,
  detail,
  detailKey,
  detailTitle,
  onCloseDetail,
}: {
  list: React.ReactNode;
  /** Выбранная запись, или `null`. */
  detail: React.ReactNode | null;
  /** Ключ выбранной записи. Меняется ключ — меняется содержимое инспектора. */
  detailKey?: string | null;
  /** Заголовок инспектора: имя того, что открыто. */
  detailTitle?: string;
  /** Крестик инспектора. Список снимает выбор. */
  onCloseDetail?: () => void;
  /** @deprecated Заглушки больше нет: пустая панель не рисуется. */
  placeholderIcon?: string;
  /** @deprecated */
  placeholderTitle?: string;
  /** @deprecated */
  placeholderHint?: string;
}) {
  const p = useTheme();
  const setEntry = useInspectorHost();

  /*
    Тело панели читается через ссылку, а не захватывается эффектом: хозяин зовёт
    `render()` на своём рендере и получает то, что список нарисовал последним. Эффект
    же срабатывает только на СМЕНУ ключа — иначе каждый рендер списка (пришли данные,
    поменялся фильтр) перерегистрировал бы запись и гонял бы страницу по кругу.
  */
  const renderRef = useRef<() => React.ReactNode>(() => detail);
  renderRef.current = () => detail;
  const closeRef = useRef(onCloseDetail);
  closeRef.current = onCloseDetail;

  const key = detail ? detailKey ?? 'detail' : null;

  useEffect(() => {
    if (!setEntry) {
      return;
    }
    if (!key) {
      setEntry(null);
      return;
    }
    setEntry({
      key,
      title: detailTitle,
      render: () => renderRef.current(),
      onClose: () => closeRef.current?.(),
    });
  }, [setEntry, key, detailTitle]);

  // Список ушёл с экрана (сменили вкладку) — панель уходит вместе с ним.
  useEffect(() => () => setEntry?.(null), [setEntry]);

  if (setEntry || !detail) {
    return <View style={styles.list}>{list}</View>;
  }

  return (
    <View style={styles.root}>
      <View style={styles.list}>{list}</View>
      <View style={[styles.pane, {backgroundColor: p.card, borderColor: p.border}]}>
        {detail}
      </View>
    </View>
  );
}
